import type {
  FocusEventHandler,
  FunctionComponent,
  SyntheticEvent,
} from 'react'
import React from 'react'

import classnames from 'classnames'

import type { AlertTypes } from '../../../AlertBadge'
import { AlertBadge } from '../../../AlertBadge'
import { AnimationWrapper } from '../../../AnimationWrapper'
import type { BadgeTheme } from '../../../Badge'
import type { ProductColour } from '../../../ColourSwatch/ColourSwatchGroup'
import { ColourSwatchGroup } from '../../../ColourSwatch/ColourSwatchGroup'
import { BaseLink } from '../../../Links'
import type { PictureProps } from '../../../Picture'
import { Picture } from '../../../Picture'
import type { ProductScoreGaugeProps } from '../../../ProductScoreGauge'
import type { ProsConsProps } from '../../../ProsCons'
import { SaveButton } from '../../../SaveButton'
import { TypographyV2 } from '../../../Seatbelt2/Foundations/Typography'
import type { TooltipWithButtonProps } from '../../../Tooltip'
import { PlaceholderImage } from '../shared/PlaceholderImage/PlaceholderImage'
import { ProductCardAdditionalInfo } from '../shared/ProductCardAdditionalInfo'
import { ProductCardBadges } from '../shared/ProductCardBadges'
import type { CCBProps } from '../shared/ProductCardCompareCheckbox/ProductCardCompareCheckbox'
import { ProductCardCompareCheckbox } from '../shared/ProductCardCompareCheckbox/ProductCardCompareCheckbox'
import { ProductCardPrice } from '../shared/ProductCardPrice/ProductCardPrice'
import { ProductCardTags } from '../shared/ProductCardTags/ProductCardTags'
import { AnnualRunningCost } from './AnnualRunningCost/AnnualRunningCost'
import styles from './ProductCard.module.scss'

export const ProductCard: FunctionComponent<ProductCardProps> = ({
  image,
  manufacturer,
  model,
  badges,
  tags,
  price,
  slug,
  retailers,
  alertType,
  handleAddCompare,
  handleRemoveCompare,
  handleFocus,
  prosConsList,
  productScoreGauge,
  isSelectedForComparison,
  comparisonLimitReached,
  cardCallback,
  productUrl,
  taxonomySlug,
  businessKey,
  viewRetailersCallback,
  annualRunningCost,
  variants,
  selectedVariant,
  handleVariantChange,
  saved = false,
  pulsateSaveIcon = false,
  saveOnClick,
  showCompare,
}) => {
  const compareCheckboxProps = {
    dataTestId: 'compare-checkbox',
    manufacturer: manufacturer,
    model: model,
    slug: slug,
    handleAddCompare: handleAddCompare,
    handleRemoveCompare: handleRemoveCompare,
    businessKey: businessKey,
    isSelectedForComparison: isSelectedForComparison,
    comparisonLimitReached: comparisonLimitReached,
    showCompare,
  }

  return (
    // TODO: create tickets to fix non-interactive element warning
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <article
      data-testid="sb-product-card"
      onClick={cardCallback}
      onKeyDown={(e) => e.key === 'Enter' && cardCallback(e)}
      onFocus={(e) => handleFocus && handleFocus(e)}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      className={styles.card}
      aria-label={`${manufacturer} ${model}`}
      data-url={`Find this product at this URL: (${productUrl})`}
    >
      <div className={styles.wrapper}>
        <ProductCardBadges badges={badges} />
        <div className={styles.information}>
          <div className={styles.imageCompareContainer}>
            <div className={styles.imageContainer}>
              <AlertBadge type={alertType} className={styles.safetyAlert} />
              {image?.src ? (
                <Picture {...image} className={styles.image} />
              ) : (
                <PlaceholderImage />
              )}
            </div>
            {saveOnClick && (
              <div className={styles.saveButton}>
                <SaveButton
                  selected={saved}
                  onClick={saveOnClick}
                  pulsate={pulsateSaveIcon}
                  productName={`${manufacturer} ${model}`}
                />
              </div>
            )}
            <ProductCardCompareCheckbox
              {...compareCheckboxProps}
              className={styles.compareDesktop}
              type="desktop"
            />
          </div>
          <div className={styles.details} data-testid="product-details">
            <BaseLink
              href={`/reviews/${taxonomySlug}/${slug}`}
              invertedUnderline={true}
              onClick={(e: React.MouseEvent) => e.preventDefault()}
              tabIndex={-1}
            >
              <TypographyV2
                tag="h3"
                textStyle="sb-text-body-default-strong"
                className={classnames(
                  styles.manufacturer,
                  saveOnClick && styles.rightPadding
                )}
              >
                <AnimationWrapper inverted={true}>
                  {manufacturer}
                </AnimationWrapper>
                <TypographyV2
                  textStyle="sb-text-body-default-regular"
                  className={styles.model}
                >
                  {model}
                </TypographyV2>
              </TypographyV2>
            </BaseLink>
            <ProductCardTags tags={tags} className={styles.tags} />
            {variants && handleVariantChange && (
              <ColourSwatchGroup
                name={`colour-swatch-group-${businessKey}`}
                variants={variants}
                selectedVariant={selectedVariant as ProductColour[]}
                onChange={handleVariantChange}
              />
            )}
            <ProductCardPrice
              {...{
                price,
                productUrl,
                retailers,
                slug,
                viewRetailersCallback,
                manufacturer,
                model,
              }}
              className={styles.price}
            />
            <ProductCardCompareCheckbox
              {...compareCheckboxProps}
              className={styles.compareTablet}
              type="tablet"
            />
          </div>
        </div>
        <ProductCardAdditionalInfo
          productScoreGauge={{ ...productScoreGauge, variant: 'compact' }}
          prosConsList={prosConsList}
        />
        <ProductCardPrice
          {...{
            price,
            productUrl,
            retailers,
            slug,
            viewRetailersCallback,
            manufacturer,
            model,
          }}
          className={styles.mobileOnly}
          priceClassName={styles.noMargin}
        />
        <ProductCardCompareCheckbox
          {...compareCheckboxProps}
          className={styles.compareMobile}
          type="mobile"
        />
      </div>
      {annualRunningCost && <AnnualRunningCost {...annualRunningCost} />}
    </article>
  )
}

///////// IMPLEMENTATION /////////

export type ProductCardProps = {
  /**
   *  Product image.
   */
  image: PictureProps
  /**
   *  Product manufacturer.
   */
  manufacturer: string
  /**
   *  Product model.
   */
  model: string
  /**
   *  Product price.
   */
  price: string
  /**
   *  Internal hyperlink to product detail page.
   */
  slug: string
  /**
   * optional focus event handler
   */
  handleFocus?: FocusEventHandler<HTMLElement>
  /**
   *  Pros and cons array.
   */
  prosConsList?: {
    pros?: ProsConsProps | null
    cons?: ProsConsProps | null
  } | null
  /**
   *  Test score gauge.
   */
  productScoreGauge: ProductScoreGaugeProps
  /**
   *  Optional list of badges.
   */
  badges?: Array<BadgeTheme> | null
  /**
   *  Optional list of tags.
   */
  tags?: Array<string> | null
  /**
   *  Optional boolean to render internal retailers link.
   */
  retailers?: boolean
  /**
   *  Type of alert badge that should be rendered wth priority over other alerts
   */
  alertType?: AlertTypes | null
  /**
   * callback that will be fired when the Product Card is clicked on
   */
  cardCallback: (
    e: React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent
  ) => void
  /**
   * full product url to be displayed within print view inside the card
   */
  productUrl: string
  /**
   * Product taxonomy slug
   */
  taxonomySlug: string
  /**
   * Product businessKey
   */
  businessKey: string
  /**
   * Optional callback to be called when the View Retailers link is clicked
   */
  viewRetailersCallback?: (e: SyntheticEvent) => void
  /**
   * The annual running cost of a product
   */
  annualRunningCost?: AnnualRunningCostProps
  /**
   * Optional variants
   */
  variants?: ProductColour[][] | null
  /**
   * Optional selected variant
   */
  selectedVariant?: ProductColour[] | null
  /**
   * Optional callback to be called when colour variant is selected
   */
  handleVariantChange?: (selectedVariant: ProductColour[]) => void
  /**
   * Boolean to indicate if the user saved this product
   */
  saved: boolean
  /**
   * Boolean to indicate if we need to animate the saved icon
   */
  pulsateSaveIcon?: boolean
  /**
   * Callback function for when the heart icon is clicked
   */
  saveOnClick?: () => void
} & Pick<
  CCBProps,
  | 'comparisonLimitReached'
  | 'handleAddCompare'
  | 'handleRemoveCompare'
  | 'isSelectedForComparison'
  | 'showCompare'
>

export type AnnualRunningCostProps = {
  priceLabel: string | JSX.Element
  ranking?: JSX.Element
  tooltip?: TooltipWithButtonProps
}
